import { theme } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectLockersLogsByPersonID } from 'reduxStore/lockersLogs/selectors';
import { TableFilterDropdown } from 'zComponents/atoms';
import { Table, Tag, Typography } from 'zui/Antd';

const { Text } = Typography;

const AccessLogsContent = ({ personID }) => {
	const {
		token: { fontWeightStrong, fontSizeSM }
	} = theme.useToken();

	// Redux state
	const lockerLogs = useSelector((state) => selectLockersLogsByPersonID(state, personID));
	const devices = useSelector(selectDevices);
	const { loading } = useSelector((state) => state.LockersLogs);

	// Local state

	// Helpers
	const getLockMac = (device) => device?.fields?.find((f) => f.fieldTitle === 'Lock Mac')?.value || '';

	// Fast lookup maps (O(1) access)
	const lockerNameByMac = useMemo(() => {
		const map = new Map();
		for (const d of devices) {
			const mac = getLockMac(d);
			if (mac) map.set(mac, d.name || mac);
		}
		return map;
	}, [devices]);

	const statusOptions = useMemo(() => {
		const statuses = lockerLogs.map((log) => (log.success ? 'Success' : 'Error'));
		const uniqueStatuses = [...new Set(statuses)];
		return uniqueStatuses.map((status) => ({ label: status, value: status }));
	}, [lockerLogs]);

	const lockerOptions = useMemo(() => {
		const uniqueLockMacs = [...new Set(lockerLogs.map((log) => log.lockMac).filter(Boolean))];
		return uniqueLockMacs
			.map((mac) => ({
				label: lockerNameByMac.get(mac) || mac,
				value: mac
			}))
			.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [lockerLogs, lockerNameByMac]);

	const columns = [
		{
			title: 'DATE - TIME',
			dataIndex: 'createdDate',
			key: 'createdDate',
			render: (text) => dayjs(text).format('D/MM/YYYY - h:mm A'),
			sorter: (a, b) => dayjs(a.createdDate).valueOf() - dayjs(b.createdDate).valueOf(),
			disableFilter: true
		},
		// {
		// 	title: 'ACTION',
		// 	dataIndex: 'action',
		// 	key: 'action',
		// 	render: (action) => action || 'N/A',
		// 	sorter: (a, b) => (a.action || '').localeCompare(b.action || '', undefined, { sensitivity: 'base' }),
		// 	onFilter: (value, record) => record.action === value,
		// 	filterDropdown: (props) => (
		// 		<TableFilterDropdown {...props} options={actionOptions} placeholder='Search actions' />
		// 	)
		// },
		{
			title: 'STATUS',
			dataIndex: 'success',
			key: 'status',
			render: (success) => {
				const status = success ? 'Success' : 'Error';
				const color = success ? 'success' : 'error';
				return (
					<Tag style={{ border: 'none', fontWeight: fontWeightStrong, fontSizeSM }} color={color}>
						{status}
					</Tag>
				);
			},
			sorter: (a, b) => {
				const statusA = a.success ? 'Success' : 'Error';
				const statusB = b.success ? 'Success' : 'Error';
				return statusA.localeCompare(statusB, undefined, { sensitivity: 'base' });
			},
			onFilter: (value, record) => {
				const status = record.success ? 'Success' : 'Error';
				return status === value;
			},
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={statusOptions} placeholder='Search status' />
			)
		},
		{
			title: 'LOCKER',
			dataIndex: 'lockMac',
			key: 'locker',
			render: (lockMac) => <Text>{lockerNameByMac.get(lockMac) || lockMac}</Text>,
			sorter: (a, b) =>
				(lockerNameByMac.get(a.lockMac) || a.lockMac).localeCompare(
					lockerNameByMac.get(b.lockMac) || b.lockMac,
					undefined,
					{ sensitivity: 'base' }
				),
			onFilter: (value, record) => record.lockMac === value,
			filterDropdown: (props) => (
				<TableFilterDropdown {...props} options={lockerOptions} placeholder='Search lockers' />
			)
		}
		// {
		// 	title: 'PERSON',
		// 	dataIndex: 'personID',
		// 	key: 'person',
		// 	render: (personID) => {
		// 		const name = personNameById.get(personID) || '';
		// 		const person = people.find((p) => p.personID === personID);
		// 		return (
		// 			<Flex align='center' gap={8}>
		// 				<PersonProfileImage
		// 					size={26}
		// 					photoUrl={person?.photoUrl || undefined}
		// 					name={name}
		// 					entityName='People'
		// 					shape='circle'
		// 					edit={false}
		// 				/>
		// 				<Text>{name}</Text>
		// 			</Flex>
		// 		);
		// 	},
		// 	sorter: (a, b) =>
		// 		(personNameById.get(a.personID) || '').localeCompare(personNameById.get(b.personID) || '', undefined, {
		// 			sensitivity: 'base'
		// 		}),
		// 	onFilter: (value, record) => record.personID === value,
		// 	filterDropdown: (props) => (
		// 		<TableFilterDropdown {...props} options={peopleOptions} placeholder='Search people' />
		// 	)
		// }
	];

	return (
		<Table
			loading={loading}
			dataSource={lockerLogs}
			columns={columns}
			pagination={{ pageSize: 5 }}
			bordered={false}
		/>
	);
};

export default AccessLogsContent;
